/** @jsxRuntime automatic */
import { createContext, useContext, type HTMLAttributes, type ReactNode, type Ref } from "react";

export type SectionHeadingTag = "h2" | "h3" | "h4";

const SectionHeadingLevel = createContext<2 | 3 | 4>(3);

/**
 * The heading level of the sections inside a reusable surface (a11y-15). The
 * opportunity detail is a page of its own (title h1, sections h2) or a pane
 * next to the inbox (title h2, sections h3): the surface sets the level once
 * and its `SectionHeading`s and `Disclosure`s follow, so the outline never
 * jumps from h1 to h3.
 */
export function SectionHeadingLevelProvider({ level, children }: { level: 2 | 3 | 4; children: ReactNode }) {
  return <SectionHeadingLevel.Provider value={level}>{children}</SectionHeadingLevel.Provider>;
}

export interface SectionHeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** React 19 passes it as a prop: it lands on the heading. */
  ref?: Ref<HTMLHeadingElement>;
  /** A fixed level instead of the surface's (a `Disclosure` with `as`). */
  as?: SectionHeadingTag;
  /** One level under the section's (a subtitle inside a section). */
  sub?: boolean;
}

/** A section heading at the surface's level (h3 outside any provider). */
export function SectionHeading({ as, sub = false, ...props }: SectionHeadingProps) {
  const surface = useContext(SectionHeadingLevel);
  const level = as ? Number(as.slice(1)) : surface + (sub ? 1 : 0);
  if (level === 2) return <h2 {...props} />;
  if (level === 3) return <h3 {...props} />;
  if (level === 4) return <h4 {...props} />;
  return <h5 {...props} />;
}
